import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { daysBetween, dueDateFromTerms } from "../lib/invoiceTerms";
import type { Contact, Invoice, InvoiceItemWrite, InvoiceWrite, Tax } from "../api/types";

/** One editable line, held as text so a half-typed "12." is not coerced. */
type Line = {
  description: string;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  tax: string;
};

function blankLine(): Line {
  return { description: "", quantity: "1.00", unit_price: "", discount_amount: "", tax: "" };
}

/**
 * Create a draft invoice, or edit one.
 *
 * An issued invoice is immutable (FR-23.1), so the edit path refuses anything
 * that is not a draft rather than letting the form load and the save 400. The
 * server recomputes every derived money field from the lines; the client only
 * supplies the inputs.
 */
export function InvoiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const creating = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const invoiceQuery = useQuery({
    queryKey: ["invoice", id],
    queryFn: ({ signal }) => api.invoice(id!, signal),
    enabled: !creating,
  });
  const taxesQuery = useQuery({
    queryKey: ["taxes"],
    queryFn: ({ signal }) => api.taxes(signal),
    staleTime: 5 * 60_000,
  });
  // A draft is billed to a contact, so creating one needs the customer list.
  const contactsQuery = useQuery({
    queryKey: ["contacts", "invoice-picker"],
    queryFn: ({ signal }) => api.contacts({ page_size: 100 }, signal),
    enabled: creating,
    staleTime: 5 * 60_000,
  });

  if (!creating && invoiceQuery.isPending) {
    return (
      <>
        <BackLink />
        <div aria-busy="true" className="space-y-6">
          <Card>
            <Skeleton className="h-6 w-48" />
          </Card>
          <Card>
            <Skeleton className="h-40 w-full" />
          </Card>
        </div>
      </>
    );
  }

  if (!creating && (invoiceQuery.isError || !invoiceQuery.data)) {
    return (
      <>
        <BackLink />
        <PageHeader title="Could not load invoice" />
        <Card>
          <EmptyState
            title="That invoice is not available"
            body="It may have been deleted, or it belongs to another account."
            action={
              <Link to="/billing/invoices" className="text-sm font-medium underline">
                Back to invoices
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  const invoice = invoiceQuery.data;

  if (!creating && invoice && invoice.status !== "draft") {
    return (
      <>
        <BackLink />
        <PageHeader
          title={invoice.number || "Issued invoice"}
          subtitle="Issued invoices cannot be edited"
        />
        <Card>
          <EmptyState
            title="This invoice has been issued"
            body="Only a draft can be edited; corrections are made with a credit note."
            action={
              <Link
                to={`/billing/invoices/${invoice.id}`}
                className="text-sm font-medium underline"
              >
                Back to the invoice
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <BackLink />
      <PageHeader
        title={creating ? "New invoice" : `Edit ${invoice?.number || "draft invoice"}`}
        subtitle={
          creating ? "Draft an invoice, then issue it." : invoice?.contact_name || undefined
        }
      />
      <InvoiceForm
        invoice={invoice}
        contacts={contactsQuery.data?.results ?? []}
        taxes={taxesQuery.data ?? []}
        currency={invoice?.currency || user?.account?.currency || "INR"}
        onCancel={() => navigate(creating ? "/billing/invoices" : `/billing/invoices/${id}`)}
        onSaved={(saved) => {
          queryClient.setQueryData(["invoice", saved.id], saved);
          void queryClient.invalidateQueries({ queryKey: ["invoices"] });
          navigate(`/billing/invoices/${saved.id}`);
        }}
      />
    </>
  );
}

function InvoiceForm({
  invoice,
  contacts,
  taxes,
  currency,
  onCancel,
  onSaved,
}: {
  invoice: Invoice | undefined;
  contacts: Contact[];
  taxes: Tax[];
  currency: string;
  onCancel: () => void;
  onSaved: (saved: Invoice) => void;
}) {
  const creating = !invoice;

  const [contact, setContact] = useState(invoice?.contact ?? "");
  const [issueDate, setIssueDate] = useState(invoice?.issue_date ?? "");
  const [dueOn, setDueOn] = useState(invoice?.due_on ?? "");
  const initialDays =
    invoice?.issue_date && invoice?.due_on
      ? daysBetween(invoice.issue_date, invoice.due_on)
      : null;
  const [paymentTerms, setPaymentTerms] = useState(
    initialDays !== null && initialDays >= 0 ? String(initialDays) : "0",
  );
  const [manualDue, setManualDue] = useState(
    initialDays === null && Boolean(invoice?.due_on),
  );
  const [placeOfSupply, setPlaceOfSupply] = useState(invoice?.place_of_supply ?? "");
  const [interState, setInterState] = useState(invoice?.inter_state ?? false);
  const [notes, setNotes] = useState(invoice?.notes ?? "");
  const [terms, setTerms] = useState(invoice?.terms ?? "");
  const [lines, setLines] = useState<Line[]>(() =>
    invoice && invoice.items.length
      ? invoice.items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price?.amount ?? "",
          discount_amount: item.discount_amount?.amount ?? "",
          tax: item.tax ?? "",
        }))
      : [blankLine()],
  );

  const save = useMutation({
    mutationFn: (body: InvoiceWrite) =>
      invoice ? api.updateInvoice(invoice.id, body) : api.createInvoice(body),
    onSuccess: onSaved,
  });

  useEffect(() => {
    if (manualDue || !issueDate) return;
    const days = Number(paymentTerms);
    if (!Number.isFinite(days) || days < 0) return;
    const next = dueDateFromTerms(issueDate, days);
    if (next) setDueOn(next);
  }, [issueDate, paymentTerms, manualDue]);

  function applyPaymentTerms() {
    setManualDue(false);
    const days = Number(paymentTerms);
    const next = issueDate && Number.isFinite(days) ? dueDateFromTerms(issueDate, days) : null;
    if (next) setDueOn(next);
  }

  function updateLine(index: number, patch: Partial<Line>) {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  const valid =
    contact !== "" && lines.length > 0 && lines.every((line) => line.description.trim() !== "");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    const items: InvoiceItemWrite[] = lines.map((line, index) => ({
      description: line.description.trim(),
      quantity: line.quantity.trim() || "1.00",
      unit_price: { amount: line.unit_price.trim() || "0.00", currency },
      discount_amount: { amount: line.discount_amount.trim() || "0.00", currency },
      tax: line.tax || null,
      sort_order: index,
    }));
    save.mutate({
      contact,
      issue_date: issueDate || null,
      due_on: dueOn || null,
      place_of_supply: placeOfSupply,
      inter_state: interState,
      notes,
      terms,
      items,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Card>
        <h2 className="mb-4 text-sm font-semibold">Invoice</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer">
            {creating ? (
              <select
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                className={INPUT}
              >
                <option value="">Select a customer…</option>
                {contacts.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.display_name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {invoice?.contact_name || "—"}
              </p>
            )}
          </Field>
          <Field label="Place of supply">
            <input
              value={placeOfSupply}
              onChange={(event) => setPlaceOfSupply(event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Issue date">
            <input
              type="date"
              value={issueDate}
              onChange={(event) => setIssueDate(event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Due date">
            <input
              type="date"
              value={dueOn}
              onChange={(event) => {
                setManualDue(true);
                setDueOn(event.target.value);
              }}
              className={INPUT}
            />
          </Field>
          <Field label="Payment terms (days)">
            <input
              inputMode="numeric"
              value={paymentTerms}
              onChange={(event) => setPaymentTerms(event.target.value)}
              className={INPUT}
            />
            <span className="mt-1 block text-xs text-slate-500">
              {manualDue ? "Due date is a manual override." : "Due date follows these terms."}
            </span>
            {manualDue ? (
              <button
                type="button"
                onClick={applyPaymentTerms}
                className="mt-1 text-xs font-medium text-slate-600 underline hover:text-slate-900"
              >
                Use payment terms
              </button>
            ) : null}
          </Field>
        </div>
        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={interState}
            onChange={(event) => setInterState(event.target.checked)}
            className="size-4 rounded border-slate-300"
          />
          Inter-state supply
        </label>
      </Card>

      <Card>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Line items</h2>
          <Button onClick={() => setLines((prev) => [...prev, blankLine()])}>
            <Plus size={14} /> Add line
          </Button>
        </div>

        <div className="space-y-3">
          {lines.map((line, index) => (
            <div key={index} className="rounded-md border border-slate-200 p-3">
              <div className="grid gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Field label="Description">
                    <input
                      value={line.description}
                      onChange={(event) => updateLine(index, { description: event.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Qty">
                    <input
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(event) => updateLine(index, { quantity: event.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Unit price">
                    <input
                      inputMode="decimal"
                      value={line.unit_price}
                      onChange={(event) => updateLine(index, { unit_price: event.target.value })}
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Discount">
                    <input
                      inputMode="decimal"
                      value={line.discount_amount}
                      onChange={(event) =>
                        updateLine(index, { discount_amount: event.target.value })
                      }
                      className={INPUT}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Tax">
                    <select
                      value={line.tax}
                      onChange={(event) => updateLine(index, { tax: event.target.value })}
                      className={INPUT}
                    >
                      <option value="">No tax</option>
                      {taxes.map((tax) => (
                        <option key={tax.id} value={tax.id}>
                          {tax.name} ({Number(tax.rate ?? 0)}%)
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                  disabled={lines.length === 1}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 size={13} /> Remove line
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Totals are recalculated from these lines when you save.
        </p>
      </Card>

      <Card>
        <h2 className="mb-4 text-sm font-semibold">Notes and terms</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Notes">
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Terms">
            <textarea
              rows={3}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
              className={INPUT}
            />
          </Field>
        </div>
      </Card>

      {save.isError ? (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {save.error instanceof ApiError ? save.error.message : "Could not save the invoice."}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || !valid}>
          {save.isPending ? "Saving…" : creating ? "Create draft" : "Save draft"}
        </Button>
        <Button onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        {!valid ? (
          <span className="text-xs text-slate-500">
            {contact === "" ? "Choose a customer." : "Every line needs a description."}
          </span>
        ) : null}
      </div>
    </form>
  );
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function BackLink() {
  return (
    <Link
      to="/billing/invoices"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All invoices
    </Link>
  );
}
